import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import {
  LightBulbIcon,
  GlobeAmericasIcon
} from '@heroicons/react/24/outline';

const TargetIcon = () => (
  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const MissionStatementPage = () => {
  const coreValues = ['Competent', 'Compassionate', 'Committed', 'Conscientious', 'Competitive'];

  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* ============================================================================
            HERO SECTION
        ============================================================================ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          <div className="absolute inset-0">
             <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
             <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
             {/* Decorative elements */}
             <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
             <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10 bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
               <TargetIcon />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Mission & <span className="text-secondary">Vision</span>
            </h1>
            <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
              Lighting the path for the leaders of tomorrow through excellence in education and character development.
            </p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* ============================================================================
            MISSION & VISION CARDS
        ============================================================================ */}
        <section className="py-20 -mt-12 relative z-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
            {/* Mission Card */}
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 group">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-8 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                <LightBulbIcon className="w-8 h-8 text-primary group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight group-hover:text-primary transition-colors">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                The Lexington Academy seeks to maintain an educational environment in which the <span className="font-semibold text-primary">entire school community</span>,
                in partnership with community based organizations and families, works to ensure that all students are
                provided with the skills to become <span className="font-semibold text-primary">lifelong learners</span>. 
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mt-4">
                Through collaboration and standards-driven instructional curricular, we will engage all students and assist them in achieving high standards.
              </p>
            </div>

            {/* Vision Card */}
            <div className="bg-primary text-white rounded-3xl p-8 md:p-12 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group">
              <div className="relative z-10">
                <div className="w-14 h-14 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center mb-8 border border-white/20">
                  <GlobeAmericasIcon className="w-8 h-8 text-secondary" />
                </div>
                <h2 className="text-3xl font-bold mb-6 tracking-tight text-white">
                  Our Vision
                </h2>
                <p className="text-xl md:text-2xl font-light leading-relaxed text-white/90">
                  To create lifelong learners that are <span className="font-semibold text-orange-400">competent</span>, 
                  <span className="font-semibold text-orange-400"> compassionate</span>, 
                  <span className="font-semibold text-orange-400"> committed</span>, 
                  <span className="font-semibold text-orange-400"> conscientious</span>, and 
                  <span className="font-semibold text-orange-400"> competitive</span> contributing citizens of the world.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================================
            CORE VALUES - THE 5 C's
        ============================================================================ */}
        <section className="py-20 md:py-24 bg-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-accent-yellow font-extrabold tracking-[0.2em] text-sm uppercase mb-3 block">Our Core Values</span>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight">The 5 C's of Success</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {coreValues.map((value, index) => (
                <div
                  key={value}
                  className="group bg-accent-yellow rounded-2xl p-4 sm:p-8 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 motion-reduce:transform-none"
                >
                  <div
                    className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary shadow-md flex items-center justify-center group-hover:bg-orange-700 transition-colors duration-300">
                    <span aria-hidden="true" className="text-3xl font-extrabold text-accent-yellow group-hover:text-white transition-colors">
                      {value.charAt(0)}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-extrabold text-primary [overflow-wrap:anywhere]">
                    {value}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================================================
            CTA SECTION
        ============================================================================ */}
        <section className="py-20 md:py-24 bg-accent-yellow">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl p-12 md:p-16 shadow-2xl text-center relative overflow-hidden bg-primary">
              <LionsStripe className="absolute top-0 inset-x-0" />
              
              <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-6">Join Our Mission</h2>
              <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
                Together, we can empower students to become the leaders and innovators of tomorrow.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/principals-message"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-extrabold text-primary bg-accent-yellow rounded-full hover:bg-white shadow-lg hover:shadow-xl hover:-translate-y-1 motion-reduce:transform-none transition-all duration-300"
                >
                  Principal's Message
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white border-2 border-white/50 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MissionStatementPage;
